/**
 * RTWO Native Currency Engine — Internal Event Bus
 */

export const NATVInternalEventTypes = {
  CURRENCY_REGISTERED: 'currency_registered',
  MONETARY_POLICY_CONFIGURED: 'monetary_policy_configured',
  SUPPLY_STATE_QUERIED: 'supply_state_queried',
  ALLOCATION_POLICY_CONFIGURED: 'allocation_policy_configured',
  FEE_POLICY_CONFIGURED: 'fee_policy_configured',
  EMISSION_SCHEDULE_CONFIGURED: 'emission_schedule_configured',
  PARAMETER_UPDATED: 'parameter_updated',
  NATIVE_CURRENCY_EVENT_PUBLISHED: 'native_currency_event_published',
  CURRENCY_VALIDATED: 'currency_validated',
} as const;

export type NATVInternalEventType =
  (typeof NATVInternalEventTypes)[keyof typeof NATVInternalEventTypes];

export interface NATVInternalEvent<T = unknown> {
  id: string;
  type: NATVInternalEventType;
  moduleId: 'RTWO-NATV-001';
  payload: T;
  timestamp: number | null;
}

export type NATVInternalEventHandler<T = unknown> = (event: NATVInternalEvent<T>) => void;

class NATVInternalEventBus {
  private subscriptions = new Map<NATVInternalEventType, NATVInternalEventHandler[]>();

  subscribe<T>(eventType: NATVInternalEventType, handler: NATVInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    handlers.push(handler as NATVInternalEventHandler);
    this.subscriptions.set(eventType, handlers);
  }

  unsubscribe<T>(eventType: NATVInternalEventType, handler: NATVInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    this.subscriptions.set(
      eventType,
      handlers.filter((entry) => entry !== handler)
    );
  }

  emit<T>(eventType: NATVInternalEventType, payload: T): string {
    const event: NATVInternalEvent<T> = {
      id: `natv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: eventType,
      moduleId: 'RTWO-NATV-001',
      payload,
      timestamp: null,
    };

    const handlers = this.subscriptions.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[NATVInternalEventBus] Handler error for ${eventType}:`, error);
      }
    }

    return event.id;
  }
}

export const natvInternalEventBus = new NATVInternalEventBus();
